import { useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";

/* ---------- Shared UI ---------- */
const CategoryCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: "16px",
  backgroundColor: selected ? alpha("#30B0C7", 0.2) : alpha("#FFFFFF", 0.9),
  backdropFilter: "blur(8px)",
  boxShadow:
    "0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)",
  cursor: "pointer",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  minHeight: "90px",

  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
  },
  ...(selected && {
    border: "2px solid #30B0C7",
  }),
}));

const WORKFORCE_DOMAINS = [
  "Accounting Skills",
  "Disaster Management",
  "Education Administration",
  "Human Resource",
  "International Relations",
  "Politics",
  "Posts in Forestry",
  "Posts in Non-Profit Organizations",
  "Social Work",
  "UPSC-Govt Organization and Ministry Posts",
  "Cooking",
  "Counter Terrorism",
  "Military",
  "Education",
  "AI-ML",
  "Cyber Security",
  "Data Science",
  "Machine Learning",
  "Software Development",
  "Virtual Reality",
  "Web Development",
  "Business Intelligence",
  "Marketing",
  "Hospitality",
  "Corporate Law",
  "Psychology",
  "Recreation",
  "Tourism",
  "Wildlife Conservation",
  "Wildlife Photography",
  "Woodworking",
  "Book Writing",
  "Content Writing",
  "Journalism",
  "Story-Screenplay-Script Writing",
  "Tech Writing",
];

/* ---------- Component ---------- */
const WorkshopQuestions = ({
  responses,
  setResponses,
  currentQuestion,
  setCurrentQuestion,
}) => {
  const topRef = useRef(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentQuestion]);

  const toggleDomain = (key, domain, max = 3) => {
    setResponses((prev) => {
      const list = prev[key].domains;
      if (list.includes(domain)) {
        return {
          ...prev,
          [key]: { ...prev[key], domains: list.filter((d) => d !== domain) },
        };
      }
      if (list.length >= max) return prev;
      return {
        ...prev,
        [key]: { ...prev[key], domains: [...list, domain] },
      };
    });
  };

  const DomainSelector = ({ questionKey, max = 3 }) => (
    <Grid container spacing={2} mt={2}>
      {WORKFORCE_DOMAINS.map((d) => (
        <Grid item xs={12} sm={6} md={4} key={d}>
          <CategoryCard
            selected={responses[questionKey].domains.includes(d)}
            onClick={() => toggleDomain(questionKey, d, max)}
          >
            <Typography>{d}</Typography>
          </CategoryCard>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box ref={topRef}>
      {/* ---------------- Q1 ---------------- */}
      {currentQuestion === 1 && (
        <>
          <Typography fontSize={22} fontWeight={600} mb={2} mt={1}>
            Q1. Domain Task Completion
          </Typography>
          <Typography fontSize={16} fontWeight={400} mb={2}>
            In how many workforce domains did you complete at least one
            evaluated task or project during the workshops?
          </Typography>
          {[
            "1–2 domains",
            "3–5 domains",
            "6–10 domains",
            "More than 10 domains",
          ].map((opt) => (
            <Button
              key={opt}
              variant={responses.q1.range === opt ? "contained" : "outlined"}
              onClick={() =>
                setResponses((p) => ({
                  ...p,
                  q1: { ...p.q1, range: opt },
                }))
              }
              sx={{ mr: 1, mt: 2 }}
            >
              {opt}
            </Button>
          ))}

          {responses.q1.range && <DomainSelector questionKey="q1" />}

          {responses.q1.domains.length > 0 && (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
                variant="contained"
                onClick={() => setCurrentQuestion(2)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      {/* ---------------- Q2 ---------------- */}
      {currentQuestion === 2 && (
        <>
          <Typography fontSize={22} fontWeight={600} mb={2} mt={1}>
            Q2. Repeated Engagement
          </Typography>
          <Typography fontSize={16} fontWeight={400} mb={2}>
            In how many workforce domains did you work on tasks in more than one
            workshop/session on sessions were advanced?
          </Typography>

          {["1 domain", "2–3 domains", "More than 3 domains"].map((opt) => (
            <Button
              key={opt}
              variant={responses.q2.range === opt ? "contained" : "outlined"}
              onClick={() =>
                setResponses((p) => ({
                  ...p,
                  q2: { ...p.q2, range: opt },
                }))
              }
              sx={{ mr: 1, mt: 2 }}
            >
              {opt}
            </Button>
          ))}

          {responses.q2.range && <DomainSelector questionKey="q2" max={3} />}

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
              variant="contained"
              onClick={() => setCurrentQuestion(1)}
            >
              Back
            </Button>
            {responses.q2.domains.length > 0 && (
              <Button
                sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
                variant="contained"
                onClick={() => setCurrentQuestion(3)}
              >
                Next
              </Button>
            )}
          </div>
        </>
      )}

      {/* ---------------- Q3 ---------------- */}
      {currentQuestion === 3 && (
        <>
          <Typography fontSize={22} fontWeight={600} mb={2} mt={1}>
            Q3. Top Domain Identification
          </Typography>
          <Typography fontSize={16} fontWeight={400} mb={2}>
            Select up to three workforce domains where your attendance, task
            completion and evaluation performance were the strongest/ highest (
            Maximum 3 selections)
          </Typography>

          <DomainSelector questionKey="q3" />

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
              variant="contained"
              onClick={() => setCurrentQuestion(2)}
            >
              Back
            </Button>
            {responses.q3.domains.length > 0 && (
              <Button
                sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
                variant="contained"
                onClick={() => setCurrentQuestion(4)}
              >
                Next
              </Button>
            )}
          </div>
        </>
      )}

      {/* ---------------- Q4 ---------------- */}
      {currentQuestion === 4 && (
        <>
          <Typography fontSize={22} fontWeight={600} mb={2} mt={1}>
            Q4. Additional Dominance
          </Typography>
          <Typography fontSize={16} fontWeight={400} mb={2}>
            In all workforce domains, choose atleast one or at max 3 in which
            you did advanced study/ projects/ etc through any digital or offline
            resources or with the help of YAPASSIO or its speakers.
          </Typography>

          <DomainSelector questionKey="q4" />

          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
              variant="contained"
              onClick={() => setCurrentQuestion(3)}
            >
              Back
            </Button>
            {responses.q4.domains.length > 0 && (
              <Button
                sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
                variant="contained"
                onClick={() => setCurrentQuestion(5)}
              >
                Next
              </Button>
            )}
          </div>
        </>
      )}

      {/* ---------------- Q5 ---------------- */}
      {currentQuestion === 5 && (
        <>
          <Typography fontSize={22} fontWeight={600} mb={2} mt={1}>
            Q5. Long-term Career Domain Decision
          </Typography>
          <Typography fontSize={16} fontWeight={400} mb={2}>
            Was there any workforce domains ( minimum 1 and at max 3 ) decided
            by you as a long term career/ goal before workshop? If yes then
            choose those workforce domains from the pre decided list which you
            think you loved and if best in workshop ( Atleast one or atmost 3 ),
            or select No if no such was decided.
          </Typography>

          {["Yes", "No"].map((opt) => (
            <Button
              key={opt}
              variant={responses.q5.decided === opt ? "contained" : "outlined"}
              onClick={() =>
                setResponses((p) => ({
                  ...p,
                  q5: { decided: opt, domains: [] },
                }))
              }
              sx={{ mr: 1, mt: 2 }}
            >
              {opt}
            </Button>
          ))}

          {responses.q5.decided === "Yes" && (
            <DomainSelector questionKey="q5" max={3} />
          )}
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{ mt: 3, mx: "auto", fontSize: "20px" }}
              variant="contained"
              onClick={() => setCurrentQuestion(4)}
            >
              Back
            </Button>
          </div>
        </>
      )}
    </Box>
  );
};

export default WorkshopQuestions;
